import { css, html, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { PassValidationCompStatus, spinner } from '../../../common/constants/api-constants';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import { updateUserProfile } from '../../user-profile/thunks/update-user-profile.thunk';
import { SecurityQuestion, UserProfile } from '../../user-profile/user-profile.interface';
import { ariaLabelWithNewTab } from '../../../common/utils/a11y-util';
import { getAnalyticsSafeData, selectSecurityQuestion, selectSecurityQuestionList, selectUser } from '../../user-profile/user-profile.selectors';
import { MyAccountFormWrapper } from '../my-account-form-wrapper';
import { fetchUserProfileQuestions } from '../../user-profile/thunks/fetch-user-questions.thunk';
import emit from '../../../common/events';
import { getAppFlow, getUserActiveTab } from '../../user-profile/utils/user-profile.util';

@customElement('mhecomm-security-field')
export class SecurityField extends connect(store)(MyAccountFormWrapper) {
  static styles = [css`
    .edit-security:focus {
      outline-color: var(--theme-gray-500) !important;
    }
  `];
  
  @query('#response')
  protected responseElement: HTMLInputElement | undefined;

  @state()
  private securityQuestionList: SecurityQuestion[] = [];

  private securityQuestion = '';

  @state()
  private responseError = false;

  @state()
  private showError = false;

  private editSecurityQuestionsButtonId = "my-account-edit-security-questions-button";
  private editSecurityQuestionsButtonLabel = "Edit"

  private cancelSecurityQuestionsButtonId = "my-account-cancel-security-questions-button";
  private cancelSecurityQuestionsButtonLabel = "Cancel"

  private updateUserSecurityQuestionsButtonId = "my-account-update-user-security-questions-button";
  private updateUserSecurityQuestionsButtonText = "Save Changes"

  private user: UserProfile | undefined;

  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    store.dispatch(fetchUserProfileQuestions());
  }

  stateChanged(state: StoreState): void {
    super.stateChanged(state);
    this.user = selectUser(state);
    this.securityQuestion = selectSecurityQuestion(state);
    this.securityQuestionList = selectSecurityQuestionList(state);
  }

  renderEditMode() {
    return html`
      <form class="needs-validation row g-2 align-items-end justify-content-end" @submit=${this.submit} novalidate>
        <div class="col-12">
          <p class="fw-bold">Security Question</p>
        </div>
        <div class="col-12 col-lg">
          <label class="form-label fw-light" for="question">Question</label>
          <select class="form-select" id="question" name="question">
            ${this.securityQuestionList.map(
              (item) =>
                html`<option value=${item.questionText} ?selected=${item.questionText === this.securityQuestion}>
                  ${item.questionText}
                </option> `
            )}
          </select>
        </div>
        <div class="col-12 col-lg">
          <label class="form-label fw-light" for="response">Answer<span class="required-mark">(required)</span></label>
          <input
            class="form-control"
            id="response"
            name="response"
            type="text"
            aria-required="true"
            @change=${() => (this.responseError = false)}
          />
        </div>
        ${this.responseError
          ? html`<div class="col-12 order-lg-5">
              <div class="alert alert-danger py-2 fs-6 invalid-feedback d-block" role="alert" aria-live="assertive">
                <uxt-icon icon="exclamation-triangle" class="me-1"></uxt-icon><span class="visually-hidden">Error&nbsp;</span>Your security answer cannot be blank.
              </div>
            </div>`
          : nothing}
        <div class="col-12 col-sm-auto">
          <button id="${this.updateUserSecurityQuestionsButtonId}" class="btn btn-primary w-100 w-sm-auto" disabled=${ifDefined(this.isSubmitted || undefined)}>
            ${this.isSubmitted ? spinner({ size: 'sm' }) : this.updateUserSecurityQuestionsButtonText}
          </button>
        </div>
        <div class="col-12 col-sm-auto">
          <button
            type="button"
            @click=${() => this.onClickCancelSecurityQuestionButton()}
            ?disabled=${this.isSubmitted || undefined}
            class="btn btn-outline-primary w-100 w-sm-auto"
          >
            Cancel
          </button>
        </div>
        ${this.showError
          ? html`<div class="col-12">
              <div class="alert alert-danger py-2 fs-6 invalid-feedback d-block" role="alert" aria-live="assertive">
                <uxt-icon icon="exclamation-triangle" class="me-1"></uxt-icon> That didn’t seem to work... We’re sorry for the
                inconvenience. Please try again, and if the problem persists reach out to
                <a href="https://mh.my.site.com/CXG/s/ContactUs" target="_blank" aria-label="${ariaLabelWithNewTab('customer support')}">customer support</a>.
              </div>
            </div>`
          : nothing}
      </form>
    `;
  }

  renderReadonlyMode() {
    return html`
      <div class="d-flex">
        <div class="flex-grow-1">
          <p class="bold">Security Question</p>
          <span class="fw-light">**********</span>
        </div>
        <div>
          ${this.isDefault()
            ? html` <button id="${this.editSecurityQuestionsButtonId}"
                class="btn btn-light-gray edit-security"
                @click=${() => this.onClickEditSecurityQuestionsButton()}
                aria-label="Edit Security Question"
              >
                ${this.editSecurityQuestionsButtonLabel}
              </button>`
            : nothing}
        </div>
      </div>
    `;
  }

  onClickEditSecurityQuestionsButton(){
    this.updateComponentStatus(PassValidationCompStatus.waiting);
    emit({
      element: this,
      name: 'click',
      detail: {
        id: this.editSecurityQuestionsButtonId,
        label:this.editSecurityQuestionsButtonLabel,
        comp: "button",
        user: getAnalyticsSafeData(this.user!),
        appFlow: getAppFlow(),
        myAccountSection: getUserActiveTab(),
      },
      dataLayer: true
    });
  }

  onClickCancelSecurityQuestionButton() {
    this.clearErrorMessages();
    this.updateComponentStatus(PassValidationCompStatus.default)
    emit({
      element: this,
      name: 'click',
      detail: {
        id: this.cancelSecurityQuestionsButtonId,
        label:this.cancelSecurityQuestionsButtonLabel,
        comp: "button",
        user: getAnalyticsSafeData(this.user!),
        appFlow: getAppFlow(),
        myAccountSection: getUserActiveTab(),
      },
      dataLayer: true
    });
  }

  async submit(e) {
    super.submit(e);
    if (this.isSubmitted) {
      return;
    }

    if (!this.responseElement?.value) {
      this.responseError = true;
      return;
    } else {
      this.responseError = false;
    }

    if (await this.isFormValid()) {
      this.isSubmitted = true;
      this.showError = false;
      const userData = this.getRawValue();

      try {
        await store.dispatch(updateUserProfile(userData)).unwrap();
        emit({
          element: this,
          name: 'submit',
          detail: {
            id: this.updateUserSecurityQuestionsButtonId,
            label:this.updateUserSecurityQuestionsButtonText,
            comp: "button",
            user: getAnalyticsSafeData(this.user!),
            appFlow: getAppFlow(),
            myAccountSection: getUserActiveTab(),
          },
          dataLayer: true
        });
      } catch (e) {
        console.error(e);
        this.showError = true;
      } finally {
        this.isSubmitted = false;
      }
    }
  }

  clearErrorMessages() {
    this.responseError = false;
    this.showError = false;
  }

  protected validationMessage() {
    return {
      question: 'Please select a security question.',
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-security-field': SecurityField;
  }
}
